import React from "react";
import {
  Save,
  CheckCircle2,
  FileText,
  Home,
  Wallet,
  UserRound,
  HandCoins,
  Info,
  Landmark,
  ShieldCheck,
  BadgePercent,
  TrendingUp,
} from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./HomebuyerReviewSubmit.css";
import TermsConditionsModal from "../../../public/legal/TermsConditionsModal";

function buildClientAdvice(draft, t) {
  const primary = getPrimaryBorrower(draft);
  const co = getCoBorrower(draft);

  const primaryFinancial = primary?.financial_info || {};
  const coFinancial = co?.financial_info || {};
  const shared = draft?.dossier_financial_info || {};
  const property = draft?.property_information || {};

  const income =
    Number(primaryFinancial.net_income || 0) +
    Number(coFinancial?.net_income || 0);

  const expenses =
    Number(primaryFinancial.monthly_expenses || 0) +
    Number(coFinancial?.monthly_expenses || 0);

  const propertyPrice = Number(shared.property_price || 0);
  const equity = Number(shared.equity_amount || 0);
  const renovationLoan = Number(shared.renovation_loan_amount || 0);
  const totalLoan = Number(shared.total_loan_amount || 0);
  const tenureYears = Number(shared.tenure_years || 0);

  const estimatedMonthlyPayment =
    tenureYears > 0 && totalLoan > 0 ? totalLoan / (tenureYears * 12) : 0;

  const monthlyBuffer = income - expenses - estimatedMonthlyPayment;
  const ownContributionPct =
    propertyPrice > 0 ? Math.round((equity / propertyPrice) * 100) : 0;
  const renovationToPropertyPct =
    propertyPrice > 0 ? Math.round((renovationLoan / propertyPrice) * 100) : 0;
  const paymentShare =
    income > 0 ? Math.round((estimatedMonthlyPayment / income) * 100) : 0;

  const currentEpc = String(property.epc_rating_current || "").toUpperCase();
  const targetEpc = String(property.epc_rating_target || "").toUpperCase();

  const advice = [];

  if (monthlyBuffer >= 600) {
    advice.push({
      key: "monthlyBufferStrong",
      status: "positive",
      icon: Wallet,
      title: t("advice.monthlyBufferStrong.title"),
      text: t("advice.monthlyBufferStrong.text"),
      value: formatMoney(monthlyBuffer),
    });
  } else if (monthlyBuffer > 0) {
    advice.push({
      key: "monthlyBufferLimited",
      status: "watch",
      icon: Wallet,
      title: t("advice.monthlyBufferLimited.title"),
      text: t("advice.monthlyBufferLimited.text"),
      value: formatMoney(monthlyBuffer),
    });
  } else {
    advice.push({
      key: "monthlyBufferRisk",
      status: "risk",
      icon: Wallet,
      title: t("advice.monthlyBufferRisk.title"),
      text: t("advice.monthlyBufferRisk.text"),
      value: "€ 0",
    });
  }

  if (paymentShare <= 30 && paymentShare > 0) {
    advice.push({
      key: "repaymentComfortable",
      status: "positive",
      icon: ShieldCheck,
      title: t("advice.repaymentComfortable.title"),
      text: t("advice.repaymentComfortable.text"),
      value: `${paymentShare}%`,
    });
  } else if (paymentShare <= 40 && paymentShare > 0) {
    advice.push({
      key: "repaymentWatch",
      status: "watch",
      icon: BadgePercent,
      title: t("advice.repaymentWatch.title"),
      text: t("advice.repaymentWatch.text"),
      value: `${paymentShare}%`,
    });
  } else if (paymentShare > 40) {
    advice.push({
      key: "repaymentHigh",
      status: "risk",
      icon: BadgePercent,
      title: t("advice.repaymentHigh.title"),
      text: t("advice.repaymentHigh.text"),
      value: `${paymentShare}%`,
    });
  }

  if (ownContributionPct >= 20) {
    advice.push({
      key: "ownContributionStrong",
      status: "positive",
      icon: HandCoins,
      title: t("advice.ownContributionStrong.title"),
      text: t("advice.ownContributionStrong.text"),
      value: `${ownContributionPct}%`,
    });
  } else if (ownContributionPct > 0) {
    advice.push({
      key: "ownContributionLimited",
      status: "watch",
      icon: HandCoins,
      title: t("advice.ownContributionLimited.title"),
      text: t("advice.ownContributionLimited.text"),
      value: `${ownContributionPct}%`,
    });
  } else {
    advice.push({
      key: "ownContributionMissing",
      status: "risk",
      icon: HandCoins,
      title: t("advice.ownContributionMissing.title"),
      text: t("advice.ownContributionMissing.text"),
      value: "—",
    });
  }

  if (renovationToPropertyPct >= 25) {
    advice.push({
      key: "renovationBudgetHigh",
      status: "watch",
      icon: Home,
      title: t("advice.renovationBudgetHigh.title"),
      text: t("advice.renovationBudgetHigh.text"),
      value: `${renovationToPropertyPct}%`,
    });
  } else if (renovationLoan > 0) {
    advice.push({
      key: "renovationBudgetBalanced",
      status: "positive",
      icon: Home,
      title: t("advice.renovationBudgetBalanced.title"),
      text: t("advice.renovationBudgetBalanced.text"),
      value: formatMoney(renovationLoan),
    });
  }

  if (currentEpc && targetEpc && currentEpc !== targetEpc) {
    advice.push({
      key: "energyImprovement",
      status: "positive",
      icon: TrendingUp,
      title: t("advice.energyImprovement.title"),
      text: t("advice.energyImprovement.text", {
        current: currentEpc,
        target: targetEpc,
      }),
      value: `${currentEpc} → ${targetEpc}`,
    });
  }

  if (String(primaryFinancial.listing_in_cpk || "").startsWith("YES")) {
    advice.push({
      key: "creditRegister",
      status: "risk",
      icon: Info,
      title: t("advice.creditRegister.title"),
      text: t("advice.creditRegister.text"),
      value: t("adviceStatus.needsReview"),
    });
  }

  if (String(primaryFinancial.contract_type || "") !== "PERMANENT") {
    advice.push({
      key: "employmentStability",
      status: "watch",
      icon: UserRound,
      title: t("advice.employmentStability.title"),
      text: t("advice.employmentStability.text"),
      value: formatEnumLabel(primaryFinancial.contract_type),
    });
  }

  const propertyCountry = String(
    draft?.country_of_property_purchase || property?.country || "",
  )
    .trim()
    .toUpperCase();

  const isSpain = propertyCountry === "ES" || propertyCountry === "SPAIN";

  if (isSpain && !property.land_register_id) {
    advice.push({
      key: "propertyDocumentation",
      status: "watch",
      icon: FileText,
      title: t("advice.propertyDocumentation.title"),
      text: t("advice.propertyDocumentation.text"),
      value: t("adviceStatus.check"),
    });
  }

  const risks = advice.filter(
    (item) => item.status === "risk" || item.status === "watch",
  );

  const positives = advice.filter((item) => item.status === "positive");

  const finalAdvice = [...risks.slice(0, 2), ...positives.slice(0, 1)];

  return finalAdvice.slice(0, 3);
}

function isFilled(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function formatMoney(value) {
  const number = Number(value || 0);
  if (!number) return "—";
  return `€ ${number.toLocaleString("en-US")}`;
}

function getPrimaryBorrower(dossierDraft) {
  const borrowers = dossierDraft?.borrowers || [];
  return borrowers.find((b) => b?.role === "PRIMARY") || borrowers[0] || null;
}

function getCoBorrower(dossierDraft) {
  const borrowers = dossierDraft?.borrowers || [];
  return borrowers.find((b) => b?.role === "CO_APPLICANT") || null;
}

function getRequirementFiles(req) {
  return req?.files || req?.document_files || [];
}

function countUploadedDocuments(requirements = []) {
  return requirements.reduce((total, req) => {
    return (
      total +
      getRequirementFiles(req).filter((file) => file?.is_active !== false)
        .length
    );
  }, 0);
}
function countRequiredDocuments(requirements = []) {
  return requirements.filter((req) => req?.is_required).length;
}

function hasExtractedData(borrower) {
  return (
    borrower?.extracted_data &&
    typeof borrower.extracted_data === "object" &&
    Object.keys(borrower.extracted_data).length > 0
  );
}

function formatEnumLabel(value) {
  if (!isFilled(value)) return "—";

  return String(value)
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getTranslatedEnum(value, baseKey, t) {
  if (!isFilled(value)) return "—";

  const translated = t(`${baseKey}.${String(value)}`, {
    defaultValue: "",
  });

  return translated || formatEnumLabel(value);
}

function getTranslatedCountry(value, t) {
  if (!isFilled(value)) return "—";

  const normalized = String(value).toUpperCase();

  if (normalized === "BE" || normalized === "BELGIUM") {
    return t("countryOptions.BELGIUM", "Belgium");
  }

  if (normalized === "ES" || normalized === "SPAIN") {
    return t("countryOptions.SPAIN", "Spain");
  }

  return formatEnumLabel(value);
}

function getDossierReferenceCode(dossierDraft) {
  return (
    dossierDraft?.dossier_code ||
    dossierDraft?.reference_code ||
    dossierDraft?.dossier_reference_code ||
    "LEG-UP-DRAFT"
  );
}

function OverviewRow({ label, value }) {
  return (
    <div className="hb-review-row">
      <span>{label}</span>
      <strong>{isFilled(value) ? value : "—"}</strong>
    </div>
  );
}

function AdviceCard({ icon: Icon, title, text, status, value, t }) {
  return (
    <div className={`hb-review-advice-card hb-review-advice-card--${status}`}>
      <div className="hb-review-advice-card__icon">
        <Icon size={16} />
      </div>

      <div className="hb-review-advice-card__content">
        <div className="hb-review-advice-card__title">{title}</div>
        <p>{text}</p>
      </div>

      <div className="hb-review-advice-card__side">
        <span>{t(`adviceStatus.${status}`)}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

// function ExtractedDataCard({ borrower, title, t }) {
//   const extracted = borrower?.extracted_data || {};
//   const countrySpecific = extracted?.country_specific || {};

//   const rows = [
//     extracted?.issuing_authority
//       ? {
//           label: t("overview.issuingAuthority"),
//           value: extracted.issuing_authority,
//         }
//       : null,
//     countrySpecific?.dni_nie
//       ? { label: t("overview.dniNie"), value: countrySpecific.dni_nie }
//       : null,
//     countrySpecific?.national_register_number
//       ? {
//           label: t("overview.nationalRegisterNumber"),
//           value: countrySpecific.national_register_number,
//         }
//       : null,
//     countrySpecific?.social_security_number
//       ? {
//           label: t("overview.socialSecurityNumber"),
//           value: countrySpecific.social_security_number,
//         }
//       : null,
//     countrySpecific?.employer_name
//       ? {
//           label: t("overview.employer"),
//           value: countrySpecific.employer_name,
//         }
//       : null,
//     countrySpecific?.annual_income
//       ? {
//           label: t("overview.annualIncome"),
//           value: formatMoney(countrySpecific.annual_income),
//         }
//       : null,
//     countrySpecific?.payslip_net_income
//       ? {
//           label: t("overview.netIncome"),
//           value: formatMoney(countrySpecific.payslip_net_income),
//         }
//       : null,
//     extracted?.model_confidence_avg
//       ? {
//           label: t("overview.confidence"),
//           value: extracted.model_confidence_avg,
//         }
//       : null,
//   ].filter(Boolean);

//   return (
//     <div className="hb-review-card">
//       <div className="hb-review-card__header">
//         <FileSearch size={16} />
//         <span>{title}</span>
//       </div>

//       {rows.length > 0 ? (
//         rows.map((row) => (
//           <OverviewRow key={row.label} label={row.label} value={row.value} />
//         ))
//       ) : (
//         <div className="hb-review-empty-state">
//           {t("status.noExtractedData")}
//         </div>
//       )}
//     </div>
//   );
// }

function hasRequiredDocumentFiles(requirement) {
  if (!requirement?.is_required) {
    return true;
  }

  const files = getRequirementFiles(requirement);

  const activeFiles = files.filter((file) => {
    if (file?.is_active === false) {
      return false;
    }

    if (file?.latest_version?.is_active) {
      return true;
    }

    return (file?.versions || []).some((version) => version?.is_active);
  });

  const minFiles = Math.max(Number(requirement?.min_files ?? 1), 1);

  return activeFiles.length >= minFiles;
}

export default function HomebuyerReviewSubmit() {
  const { t } = useTranslation("homebuyer-review-submit");

  const {
    currentStepKey,
    handleStepValidityChange,
    registerStepSubmitHandler,
    dossierDraft,
    updateDraftSection,
    openSaveModal,
  } = useOutletContext();

  const activeDossierDraft = dossierDraft || {};

  const primary = getPrimaryBorrower(activeDossierDraft);
  const coBorrower = getCoBorrower(activeDossierDraft);

  const property = activeDossierDraft?.property_information || {};
  const marketRegion =
    activeDossierDraft?.market_region || property?.market_region || null;
  const requirements = activeDossierDraft?.document_requirements || [];
  const shared = activeDossierDraft?.dossier_financial_info || {};
  const existingReview = activeDossierDraft?.review_download || {};

  const requiredRequirements = requirements.filter(
    (requirement) => requirement?.is_required,
  );

  const documentsComplete =
    requiredRequirements.length === 0 ||
    requiredRequirements.every(hasRequiredDocumentFiles);
  const dossierReferenceCode = getDossierReferenceCode(activeDossierDraft);

  const [notes, setNotes] = React.useState(existingReview.notes || "");

  const [confirmed, setConfirmed] = React.useState(!!existingReview.confirmed);

  const [acceptedTerms, setAcceptedTerms] = React.useState(
    !!existingReview.accepted_terms,
  );

  const [termsModalOpen, setTermsModalOpen] = React.useState(false);

  const [downloadDialogOpen, setDownloadDialogOpen] = React.useState(false);

  const [error, setError] = React.useState("");

  React.useEffect(() => {
    updateDraftSection("review_download", {
      notes,

      confirmed,

      confirmed_at: confirmed ? new Date().toISOString() : null,

      accepted_terms: acceptedTerms,

      accepted_terms_at: acceptedTerms ? new Date().toISOString() : null,

      dossier_reference_code: dossierReferenceCode,
    });
  }, [
    notes,
    confirmed,
    acceptedTerms,
    dossierReferenceCode,
    updateDraftSection,
  ]);

  React.useEffect(() => {
    const isValid = confirmed && acceptedTerms;

    const validationErrors = {};

    if (!confirmed) {
      validationErrors.confirmed = t("validation.confirmRequired");
    }

    if (!acceptedTerms) {
      validationErrors.accepted_terms = t(
        "validation.termsRequired",
        "You must accept the Terms and Conditions.",
      );
    }

    handleStepValidityChange(currentStepKey, isValid, validationErrors, {
      notes,

      confirmed,

      accepted_terms: acceptedTerms,

      dossier_reference_code: dossierReferenceCode,
    });
  }, [
    notes,
    confirmed,
    acceptedTerms,
    dossierReferenceCode,
    currentStepKey,
    handleStepValidityChange,
    t,
  ]);

  React.useEffect(() => {
    async function handleReviewSubmit() {
      if (!confirmed) {
        setError(t("validation.confirmBeforeDownload"));

        return {
          ok: false,
        };
      }

      if (!acceptedTerms) {
        setError(
          t(
            "validation.termsRequired",
            "You must accept the Terms and Conditions before submitting your dossier.",
          ),
        );

        return {
          ok: false,
        };
      }

      setError("");

      return {
        ok: true,

        updatedDraft: {
          ...activeDossierDraft,

          review_download: {
            ...(activeDossierDraft?.review_download || {}),

            notes,

            confirmed,

            confirmed_at: new Date().toISOString(),

            accepted_terms: true,

            accepted_terms_at: new Date().toISOString(),

            dossier_reference_code: dossierReferenceCode,
          },
        },
      };
    }

    registerStepSubmitHandler(currentStepKey, handleReviewSubmit);

    return () => {
      registerStepSubmitHandler(currentStepKey, null);
    };
  }, [
    activeDossierDraft,
    currentStepKey,
    notes,
    confirmed,
    dossierReferenceCode,
    registerStepSubmitHandler,
    acceptedTerms,
    t,
  ]);

  const uploadedDocs = countUploadedDocuments(requirements);
  const requiredDocs = countRequiredDocuments(requirements);
  const clientAdvice = React.useMemo(
    () => buildClientAdvice(activeDossierDraft, t),
    [activeDossierDraft, t],
  );

  return (
    <div className="hb-review-page">
      <div className="hb-step-page__header">
        <h1 className="hb-step-page__title">{t("title")}</h1>

        <button
          type="button"
          className="hb-app-flow__save-btn"
          onClick={openSaveModal}
        >
          <Save size={16} />
          <span>{t("buttons.save")}</span>
        </button>
      </div>

      <div className="hb-review-shell">
        <p className="hb-review-intro">{t("intro")}</p>

        <div className="hb-review-info-banner">
          <Info size={16} />
          <span>{t("sections.adviceInfo")}</span>
        </div>

        {error ? <div className="hb-review-alert">{error}</div> : null}

        <section className="hb-review-panel hb-review-panel--advice">
          <div className="hb-review-panel__title">
            {t("sections.financialAdvice")}
          </div>

          <p className="hb-review-panel__subtitle">
            {t("sections.financialAdviceHint")}
          </p>

          <div className="hb-review-advice-list">
            {clientAdvice.map((item) => (
              <AdviceCard
                key={item.key}
                icon={item.icon}
                title={item.title}
                text={item.text}
                status={item.status}
                value={item.value}
                t={t}
              />
            ))}
          </div>
        </section>

        <section className="hb-review-overview">
          <h2 className="hb-review-overview__title">
            {t("sections.dossierOverview")}
          </h2>

          <p className="hb-review-overview__subtitle">
            {t("sections.dossierOverviewHint")}
          </p>

          <div className="hb-review-cards">
            <div className="hb-review-card">
              <div className="hb-review-card__header">
                <UserRound size={16} />
                <span>{t("cards.primaryApplicant")}</span>
              </div>

              <OverviewRow
                label={t("overview.name")}
                value={[
                  primary?.personal_info?.first_name,
                  primary?.personal_info?.last_name,
                ]
                  .filter(Boolean)
                  .join(" ")}
              />
              <OverviewRow
                label={t("overview.email")}
                value={primary?.personal_info?.email}
              />
              <OverviewRow
                label={t("overview.phone")}
                value={primary?.personal_info?.phone}
              />
              <OverviewRow
                label={t("overview.country")}
                value={getTranslatedCountry(
                  primary?.personal_info?.country_of_residence,
                  t,
                )}
              />
            </div>

            {coBorrower ? (
              <div className="hb-review-card">
                <div className="hb-review-card__header">
                  <UserRound size={16} />
                  <span>{t("cards.coBorrower")}</span>
                </div>

                <OverviewRow
                  label={t("overview.name")}
                  value={[
                    coBorrower?.personal_info?.first_name,
                    coBorrower?.personal_info?.last_name,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
                <OverviewRow
                  label={t("overview.email")}
                  value={coBorrower?.personal_info?.email}
                />
                <OverviewRow
                  label={t("overview.dateOfBirth")}
                  value={coBorrower?.personal_info?.date_of_birth}
                />
                <OverviewRow
                  label={t("overview.extractedData")}
                  value={
                    hasExtractedData(coBorrower)
                      ? t("status.available")
                      : t("status.missing")
                  }
                />
              </div>
            ) : null}

            <div className="hb-review-card">
              <div className="hb-review-card__header">
                <FileText size={16} />
                <span>{t("cards.documents")}</span>
              </div>

              <OverviewRow
                label={t("overview.uploadedDocuments")}
                value={`${uploadedDocs}`}
              />
              <OverviewRow
                label={t("overview.requiredDocuments")}
                value={`${requiredDocs}`}
              />
              <OverviewRow
                label={t("overview.status")}
                value={
                  documentsComplete
                    ? t("status.complete")
                    : t("status.incomplete")
                }
              />
              <OverviewRow
                label={t("overview.extractedData")}
                value={
                  hasExtractedData(primary)
                    ? t("status.primaryAvailable")
                    : t("status.pending")
                }
              />
            </div>

            <div className="hb-review-card">
              <div className="hb-review-card__header">
                <Home size={16} />
                <span>{t("cards.propertyInformation")}</span>
              </div>

              <OverviewRow
                label={t("overview.propertyType")}
                value={getTranslatedEnum(
                  property.property_type,
                  "propertyTypeOptions",
                  t,
                )}
              />
              <OverviewRow label={t("overview.city")} value={property.city} />
              <OverviewRow
                label={t("overview.region")}
                value={marketRegion?.name || "—"}
              />
              <OverviewRow
                label={t("overview.country")}
                value={getTranslatedCountry(
                  activeDossierDraft?.country_of_property_purchase ||
                    property?.country,
                  t,
                )}
              />
              <OverviewRow
                label={t("overview.address")}
                value={property.full_address}
              />
              <OverviewRow
                label={t("overview.currentEpc")}
                value={property.epc_rating_current}
              />
            </div>

            <div className="hb-review-card">
              <div className="hb-review-card__header">
                <Wallet size={16} />
                <span>{t("cards.loanSummary")}</span>
              </div>

              <OverviewRow
                label={t("overview.totalLoan")}
                value={formatMoney(shared.total_loan_amount)}
              />
              <OverviewRow
                label={t("overview.purchaseLoan")}
                value={formatMoney(shared.purchase_loan_amount)}
              />
              <OverviewRow
                label={t("overview.renovationLoan")}
                value={formatMoney(shared.renovation_loan_amount)}
              />
              <OverviewRow
                label={t("overview.ownContribution")}
                value={formatMoney(shared.equity_amount)}
              />
              <OverviewRow
                label={t("overview.propertyPrice")}
                value={formatMoney(shared.property_price)}
              />
            </div>

            {/* <ExtractedDataCard
              borrower={primary}
              title={t("cards.primaryExtractedData")}
              t={t}
            />

            {coBorrower ? (
              <ExtractedDataCard
                borrower={coBorrower}
                title={t("cards.coBorrowerExtractedData")}
                t={t}
              />
            ) : null} */}

            <div className="hb-review-card">
              <div className="hb-review-card__header">
                <Landmark size={16} />
                <span>{t("cards.bankingCompliance")}</span>
              </div>

              <OverviewRow
                label={t("overview.repaymentAccount")}
                value={shared.repayment_bank_account}
              />
              <OverviewRow
                label={t("overview.disbursementAccount")}
                value={shared.loan_disbursement_bank_account}
              />
              <OverviewRow
                label={t("overview.tenure")}
                value={
                  isFilled(shared.tenure_years)
                    ? t("overview.tenureYearsValue", {
                        value: shared.tenure_years,
                      })
                    : "—"
                }
              />
            </div>
          </div>
        </section>

        <section className="hb-review-final">
          <div className="hb-review-final__group">
            <label className="hb-review-final__label">
              {t("final.additionalNotes")}
            </label>

            <p className="hb-review-final__hint">
              {t("final.additionalNotesHint")}
            </p>

            <textarea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("placeholders.additionalNotes")}
            />
          </div>

          <div className="hb-review-confirmation">
            {/* ===============================================
      DOSSIER CONFIRMATION
      =============================================== */}

            <div className="hb-review-confirmation__row">
              <label className="hb-review-confirmation__checkbox">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />

                <span>{t("final.confirmDossierReview")}</span>
              </label>

              {confirmed ? (
                <div className="hb-review-confirmation__accepted">
                  <CheckCircle2 size={16} />

                  <span>{t("status.confirmed")}</span>
                </div>
              ) : null}
            </div>

            {/* ===============================================
      TERMS & CONDITIONS
      =============================================== */}

            <div className="hb-review-confirmation__row">
              <label className="hb-review-confirmation__checkbox">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />

                <span>{t("final.termsPrefix", "I agree to the")} </span>
              </label>

              <button
                type="button"
                className="hb-review-terms-link"
                onClick={() => setTermsModalOpen(true)}
              >
                {t("final.termsLink", "Terms and Conditions")}
              </button>

              <span className="hb-review-terms-required" aria-hidden="true">
                *
              </span>

              {acceptedTerms ? (
                <div className="hb-review-confirmation__accepted">
                  <CheckCircle2 size={16} />

                  <span>{t("status.termsAccepted", "Accepted")}</span>
                </div>
              ) : null}
            </div>
          </div>
        </section>
      </div>
      <TermsConditionsModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
      />
    </div>
  );
}
